import { URLS } from '../navigation/navigation.data';
import { ProductFeature, ProductPictures, ShowcaseConfig } from './product-features.data';
import type { MoreToolApp, MoreToolVariant } from '../../component/more-tools/more-tools.component';

/** The four color pickers' panels, raw (the pages' stage pictures cut to the panel's own edge, the
 *  baked shadows off, the Mac panel without its menu bar notch, all 720px tall: owner 2026-10-10,
 *  every picture the same height and the same plain look). */
export const COLOR_PICKER_PICTURES: ProductPictures = { dir: './assets/product-lines/tools/color-pickers/', prefix: 'cp-' };

// One color picker on every platform, one slide each, the macOS app first (owner 2026-10-11; 2026-10-10: one card under the
// coverage matrix, the platform switched with the chips). The headline is the platform's App
// Store or Marketplace picture's own, as text; the scene is the picker itself, raw. Each slide
// brings its platform's eyebrow, icon, facts and page, so the card's left side follows the chip.
const PICKERS: ProductFeature[] = [
  {
    key: 'macos', chip: 'macOS', chipLogo: './assets/third-parties/logo/macos-apps.webp',
    accent: 'Grab Any Color,', rest: 'Right on Your Mac', layout: 'one',
    line: 'Eyedrop anything on screen from the menu bar: every color space, contrast checks and harmonies',
    description: 'Pick any color on your screen and read everything about it, right from your menu bar.',
    eyebrow: 'macOS App', eyebrowLogo: './assets/third-parties/logo/macos-apps.webp',
    icon: './assets/product-lines/tools/macos-color-picker.png',
    facts: [ 'Native Menu Bar App', 'macOS 13 Or Later', 'Apple Silicon' ],
    link: URLS.plugins.macosColorPicker,
    store: { label: 'Install from the Mac App Store', url: 'https://apps.apple.com/app/codigrate-color-picker/id6798887660', macApp: true, event: 'App Install Clicked', attributes: { app: 'macos-color-picker' } },
    // sold on the site too (2026-10-14): Add to cart and the price while the store sells it, the App Store button otherwise
    cartSku: 'color-picker/macos',
    scenes: [ { key: 'macos', name: 'Color Picker for Mac', alt: 'The Color Picker panel from the menu bar, with a picked indigo in every color space', width: 387, height: 720 } ],
  },
  {
    key: 'chrome', chip: 'Chrome', chipLogo: './assets/third-parties/logo/chrome.webp',
    accent: 'Grab Any Color,', rest: 'Right in Chrome', layout: 'one',
    line: 'Eyedrop anything on a page: every color space, contrast checks and harmonies in one popup',
    description: 'Pick any color on your screen and read it in every color space, check its contrast and explore its harmonies, right from your toolbar.',
    eyebrow: 'Chrome Extension', eyebrowLogo: './assets/third-parties/logo/chrome.webp',
    icon: './assets/product-lines/tools/chrome-color-picker.svg',
    facts: [ 'Free', 'No Sign In, No Tracking', 'Colors Stay On Your Device' ],
    link: URLS.plugins.chromeColorPicker,
    store: { label: 'Install from the Chrome Web Store', url: 'https://chromewebstore.google.com/detail/kemglmedckibbmnfmeppkgconcpckepm', event: 'Extension Install Clicked', attributes: { extension: 'color-picker' } },
    scenes: [ { key: 'chrome', name: 'Color Picker for Chrome', alt: 'The Color Picker popup in Chrome with a picked indigo in every color space, its recent colors and copy-ready values', width: 413, height: 720 } ],
  },
  {
    key: 'jetbrains', chip: 'JetBrains', chipLogo: './assets/third-parties/logo/jetbrains.webp',
    accent: 'Grab Any Color,', rest: 'Right in Your IDE', layout: 'one',
    line: 'Eyedrop anything on screen: every color space, WCAG contrast and harmonies in a tool window',
    description: 'Pick any color on your screen and read everything about it, right in your IDE.',
    eyebrow: 'JetBrains Plugin', eyebrowLogo: './assets/third-parties/logo/jetbrains.webp',
    icon: './assets/product-lines/tools/color-picker.svg',
    facts: [ 'Free', 'IntelliJ IDEA, WebStorm, PyCharm', 'Every IntelliJ-Based IDE' ],
    link: URLS.plugins.jetbrainsColorPicker,
    store: { label: 'Install from JetBrains Marketplace', url: 'https://plugins.jetbrains.com/plugin/32652-color-picker', event: 'Plugin Install Clicked', attributes: { plugin: 'jetbrains-color-picker' } },
    scenes: [ { key: 'jetbrains', name: 'Color Picker for JetBrains IDEs', alt: 'The Color Picker tool window with its hex field, recent colors and a picked blue in every color space', width: 393, height: 720 } ],
  },
  {
    key: 'vscode', chip: 'VS Code', chipLogo: './assets/third-parties/logo/vscode.webp',
    accent: 'Grab Any Color,', rest: 'Right in VS Code', layout: 'one',
    line: 'Eyedrop anything on screen or pick up a color from your code: every color space, WCAG contrast and harmonies in the side bar',
    description: 'Pick any color on your screen or in your code and read everything about it, right in your editor.',
    eyebrow: 'VS Code Extension', eyebrowLogo: './assets/third-parties/logo/vscode.webp',
    icon: './assets/product-lines/tools/color-picker.svg',
    facts: [ 'Free', 'VS Code, Cursor, Antigravity', 'Every VS Code Based Editor' ],
    link: URLS.plugins.vscodeColorPicker,
    store: { label: 'Install from VS Code Marketplace', url: 'https://marketplace.visualstudio.com/items?itemName=codigrate.cod-color-picker', event: 'Extension Install Clicked', attributes: { extension: 'vscode-color-picker', store: 'vs-marketplace' } },
    scenes: [ { key: 'vscode', name: 'Color Picker for VS Code', alt: 'The Color Picker panel in the VS Code side bar, with the color under the cursor and a picked indigo in every color space', width: 393, height: 720 } ],
  },
];

/** The home's Color Pickers card (product-showcase.component.ts): one picker, its platforms as chips. */
export const COLOR_PICKERS_SHOWCASE: ShowcaseConfig = {
  key: 'color-pickers',
  shape: 'scene',
  eyebrowLogo: PICKERS[ 0 ].eyebrowLogo as string,
  eyebrow: 'Color Picker',
  icon: PICKERS[ 0 ].icon as string,
  title: 'Color Picker',
  lead: 'Pick any color on your screen and read everything about it',
  text: 'Every color space from HEX to OKLCH, contrast checks for readable text and the harmonies of the color wheel, wherever you work: from your Mac\'s menu bar, in Chrome, in your JetBrains IDE or in VS Code.',
  facts: [],
  link: URLS.plugins.macosColorPicker,
  cta: 'Discover More',
  soon: '',
  pickerLabel: 'Color Picker platforms',
  pictures: COLOR_PICKER_PICTURES,
  slides: PICKERS,
};

/** The Color Picker under More Color Tools on the color tools (owner 2026-10-22): every platform in a dock under the
 *  panel, the Mac app first as on its Apps & Plugins box */
const pickerVariant = ( p: ProductFeature ): MoreToolVariant => ( {
  key: p.key, chip: p.chip as string, chipLogo: p.chipLogo,
  eyebrow: p.eyebrow, icon: p.icon, facts: p.facts as string[], link: p.link, store: p.store, cartSku: p.cartSku,
  shot: { src: `./assets/product-lines/tools/color-pickers/cp-${ p.key }.webp`, alt: p.scenes[ 0 ].alt, width: p.scenes[ 0 ].width, height: p.scenes[ 0 ].height },
} );

export const COLOR_PICKER_MORE_TOOL: MoreToolApp = {
  ...( pickerVariant( PICKERS[ 0 ] ) as MoreToolApp ),
  name: 'Color Picker',
  lead: COLOR_PICKERS_SHOWCASE.lead,
  text: COLOR_PICKERS_SHOWCASE.text,
  cta: 'Discover More',
  variants: PICKERS.map( pickerVariant ),
};
